import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosInstance, AxiosResponse } from "axios";
import { useApiClient } from "@/context/ApiContext";
import { ODataParams } from "@/utils/odataUtil";
import { DEBOUNCE_MS, PAGE_SIZE } from "@/utils/constants";

type SortState = { key: string; direction: 'asc' | 'desc' } | null;
type PageState = { skip: number; top: number };

interface UseStaffingTableOptions<TFilters extends { name: string }> {
    region: string | null;
    defaultFilters: TFilters;
    buildFilter: (region: string, filters: TFilters) => string;
    defaultOrderBy: string;
    fetcher: (params: ODataParams, apiClient: AxiosInstance) => Promise<AxiosResponse>;
}

interface UseStaffingTableResult<TData, TFilters> {
    data: TData[];
    loading: boolean;
    error: string | null;
    total: number;
    filters: TFilters;
    sort: SortState;
    applyFilter: (next: TFilters) => void;
    handleNameChange: (name: string) => void;
    handleParamsChange: (sort: SortState, page: PageState) => void;
}

export function useStaffingTable<TData, TFilters extends { name: string }>(
    options: UseStaffingTableOptions<TFilters>
): UseStaffingTableResult<TData, TFilters> {
    const { region, defaultFilters, buildFilter, defaultOrderBy, fetcher } = options;
    const apiClient = useApiClient();
    const [data, setData] = useState<TData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState<TFilters>(defaultFilters);
    const [sort, setSort] = useState<SortState>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetch = useCallback((activeFilters: TFilters, activeSort: SortState, activePage: PageState) => {
        if (!region) return;
        setLoading(true);
        setError(null);
        const params: ODataParams = {
            filter: buildFilter(region, activeFilters),
            orderby: activeSort ? `${activeSort.key} ${activeSort.direction}` : defaultOrderBy,
            skip: activePage.skip,
            top: activePage.top,
            count: true,
        };
        fetcher(params, apiClient)
            .then((res) => {
                setData(res.data.value);
                setTotal(res.data['@odata.count'] ?? 0);
            })
            .catch((err) => {
                console.error(err);
                setError('Failed to load data. Please try again.');
            })
            .finally(() => setLoading(false));
    }, [region, buildFilter, defaultOrderBy, fetcher, apiClient]);

    useEffect(() => {
        setFilters(defaultFilters);
        setSort(null);
        fetch(defaultFilters, null, { skip: 0, top: PAGE_SIZE });
    }, [region, fetch, defaultFilters]);

    const applyFilter = (next: TFilters) => {
        setFilters(next);
        fetch(next, sort, { skip: 0, top: PAGE_SIZE });
    };

    const handleNameChange = (name: string) => {
        const next = { ...filters, name };
        setFilters(next);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetch(next, sort, { skip: 0, top: PAGE_SIZE });
        }, DEBOUNCE_MS);
    };

    const handleParamsChange = (activeSort: SortState, activePage: PageState) => {
        setSort(activeSort);
        fetch(filters, activeSort, activePage);
    };

    return { data, loading, error, total, filters, sort, applyFilter, handleNameChange, handleParamsChange };
}
